import { VolumeMode } from '../../database/schema.js';
import { calculateCompoundingTargets } from './compounding.js';
/**
 * The concentration preview shares the compounding engine's drug-volume math: each
 * substance is mapped into the engine's substance schema and the workflows resolve
 * the final volume (AD fills up to the carrier's volume — also into empty containers —
 * IN adds the drug volume to a full bag).
 *
 * Prescription previews differ from cleanroom compounding:
 *  - a substance without a product cannot enter the engine payload; the carrier
 *    volume alone is used and the result marked approximate (IN mode only — the AD
 *    carrier volume is the final volume regardless)
 *  - any engine rejection (incomplete trade name data etc.) degrades the same way.
 */
export function calculatePreparationConcentration(input) {
    const carrierVolumeMl = input.carrierVolumeMl ?? 0;
    if (carrierVolumeMl <= 0)
        return null;
    const volumeMode = input.volumeMode ?? VolumeMode.enum.AD;
    const substances = (input.substances ?? []).filter((substance) => typeof substance.totalAmount === 'number' && substance.totalAmount > 0);
    const totalAmount = substances.reduce((acc, substance) => acc + substance.totalAmount, 0);
    if (totalAmount <= 0)
        return null;
    // IN with an unpicked product: the drug volume will come on top once the
    // product is chosen — the carrier volume alone is an approximation
    if (substances.some(substance => !substance.drug)) {
        return carrierBasedResult(carrierVolumeMl, totalAmount, volumeMode);
    }
    const targets = calculateTargets(substances, carrierVolumeMl, volumeMode);
    if (!targets) {
        return carrierBasedResult(carrierVolumeMl, totalAmount, volumeMode);
    }
    const finalVolumeMl = targets.finalVolumeMl ?? 0;
    return finalVolumeMl > 0
        ? { finalVolumeMl, concentrationPerMl: totalAmount / finalVolumeMl, approximate: false }
        : carrierBasedResult(carrierVolumeMl, totalAmount, volumeMode);
}
function carrierBasedResult(carrierVolumeMl, totalAmount, volumeMode) {
    return {
        finalVolumeMl: carrierVolumeMl,
        concentrationPerMl: totalAmount / carrierVolumeMl,
        approximate: volumeMode === VolumeMode.enum.IN,
    };
}
function calculateTargets(substances, carrierVolumeMl, volumeMode) {
    try {
        return calculateCompoundingTargets({
            // the preview only ever fills up to or tops a carrier bag, so a filled
            // container is the coherent stand-in for both volume modes
            preparation: {
                carrierRequired: true,
                carrier: { nominalVolumeMl: carrierVolumeMl, densityGcm3: 1 },
                volumeMode,
                containerContents: 'FILLED',
            },
            substances: substances.map(substance => ({
                prescription: { prescribedDose: substance.totalAmount },
                tradeName: getCompoundingTradeName(substance.drug, substance.doseUnitUcum ?? undefined),
            })),
        });
    }
    catch {
        return undefined;
    }
}
function getCompoundingTradeName(drug, doseUnitUcum) {
    const density = drug?.density ? Number(drug.density) : 1;
    const doseUnit = (doseUnitUcum || 'mg').replace(/[\s.[\]]/g, '');
    const strength = drug?.strengthValue ?? 0;
    const name = drug?.name || 'product';
    if (drug?.isSolid) {
        const solvents = (drug.solvents ?? []).flatMap(solvent => typeof solvent.volumeMl === 'number' && solvent.volumeMl > 0
            ? [{ name: solvent.name || 'solvent', volumeMl: solvent.volumeMl }]
            : []);
        return {
            form: 'SOLID',
            strengthValue: strength,
            strengthUnit: (drug.strengthUnitUcum || doseUnitUcum || 'mg').replace(/[\s.[\]]/g, ''),
            name,
            doseUnit: doseUnit,
            densityGcm3: density,
            reconstitutionRequired: Boolean(drug.reconstitutionRequired),
            ...(solvents.length ? { reconstitutionSolvents: solvents } : {}),
            ...(drug.reconstitutedSolutionConcentration ? { reconstitutedSolutionConcentration: drug.reconstitutedSolutionConcentration } : {}),
            ...(drug.reconstitutedConcentrationUnitUcum
                ? { reconstitutedConcentrationUnit: drug.reconstitutedConcentrationUnitUcum.replace(/[\s.[\]]/g, '') }
                : {}),
            ...(drug.reconstitutedSolutionVolumeExpansion != null
                ? { volumeExpansionMl: drug.reconstitutedSolutionVolumeExpansion }
                : {}),
        };
    }
    const volumeValue = drug?.volumeValue ?? 0;
    return {
        form: 'LIQUID',
        strengthValue: volumeValue ? strength / volumeValue : strength,
        strengthUnit: (drug?.strengthUnitUcum || doseUnitUcum || 'mg').replace(/[\s.[\]]/g, ''),
        name,
        doseUnit: doseUnit,
        densityGcm3: density,
    };
}
//# sourceMappingURL=preparation.js.map